import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { useAdminContext } from '@/features/admin/session'
import { cn } from '@/lib/cn'
import { formatTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'

import { useFloorPlan, useLiveOverview } from '../api'
import type {
  LiveOverview,
  OverviewAlert,
  OverviewContest,
  OverviewEntries,
  OverviewProgram,
  OverviewSales,
  OverviewStations,
  OverviewTables,
  OverviewTill,
  OverviewTills,
} from '../types'

/**
 * Régie du soir J — la vue d'ensemble du propriétaire.
 *
 * Celui qui répond de la soirée n'est à aucun poste : il doit les voir tous.
 * La porte, la cuisine, le bar, chaque tiroir, l'argent encore en poche, ce
 * qui est épuisé, le scrutin — et, en tête, ce qui déraille, avec le lien vers
 * l'écran qui le règle. Rien ne se saisit ici : on regarde, on va réparer
 * ailleurs.
 *
 * Une seule requête (`/ops/live/overview`) : chaque section n'arrive que si
 * le rôle a le droit de la lire, et disparaît sinon — jamais de zéros qui
 * feraient croire à une caisse vide.
 *
 * Sur téléphone, une colonne ; aucun tableau large, aucune ligne qui déborde :
 * chaque chiffre a son libellé au-dessus de lui, et les listes passent à la
 * ligne plutôt que de pousser la page sur le côté.
 */
export function LivePage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('live.overline')}>
      {(_session, signOut) => <LiveBoard onSignOut={signOut} />}
    </StaffGate>
  )
}

type Money = (minor: number) => string

function LiveBoard({ onSignOut }: { onSignOut: () => void }) {
  const { t, i18n } = useTranslation()
  // Même résolution de l'édition que le back-office et le bilan : la régie
  // s'ouvre aussi la veille, sur une édition encore en brouillon.
  const { event, eventId } = useAdminContext()
  const overview = useLiveOverview(eventId)

  if (isForbidden(overview.error)) {
    return <NoAccess title={t('live.overline')} onSignOut={onSignOut} />
  }

  if (overview.isPending) {
    return (
      <Container className="py-12">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-8 h-40 w-full" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Container>
    )
  }

  if (!overview.data) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-md text-content-primary">
          {t('live.unavailable')}
        </h1>
        <p className="mt-4 text-content-secondary">{t('live.unavailableHelp')}</p>
      </Container>
    )
  }

  const live = overview.data
  const money: Money = (minor) =>
    formatMoney({ amount_minor: minor, currency: live.currency }, i18n.language)
  const time = (iso: string) => formatTime(iso, i18n.language, live.timezone)

  return (
    <Container className="py-6 sm:py-8">
      <Header
        live={live}
        name={event?.name ?? live.event_name}
        updatedAt={overview.dataUpdatedAt}
        stale={overview.isError}
        fetching={overview.isFetching}
        onRefresh={() => void overview.refetch()}
      />

      <div className="mt-6 grid items-start gap-4 lg:grid-cols-5">
        {live.program ? (
          <ProgramCard program={live.program} time={time} className="lg:col-span-3" />
        ) : null}
        <AlertsCard
          alerts={live.alerts}
          money={money}
          className={live.program ? 'lg:col-span-2' : 'lg:col-span-5'}
        />
      </div>

      <div className="mt-4 grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        {live.entries ? <EntriesCard entries={live.entries} /> : null}
        {live.stations && live.stations.stations.length > 0 ? (
          <StationsCard stations={live.stations} />
        ) : null}
        {live.stations ? <WaitersCard stations={live.stations} money={money} /> : null}
        {live.tills ? <TillsCard tills={live.tills} money={money} time={time} /> : null}
        {live.tills?.holders ? <HoldersCard tills={live.tills} money={money} /> : null}
        {live.sales ? <SalesCard sales={live.sales} money={money} /> : null}
        {live.stock ? <StockCard stock={live.stock} /> : null}
        {live.tables ? <TablesCard tables={live.tables} /> : null}
        {live.contest ? <ContestCard contest={live.contest} time={time} /> : null}
      </div>

      {live.tables ? <FloorDetails eventId={eventId} /> : null}
    </Container>
  )
}

// ── En-tête ────────────────────────────────────────────────────────

/** Les écrans où l'on va réparer — pour ne pas chercher une URL à 1 h du matin. */
const SCREENS: { to: string; key: string }[] = [
  { to: '/ops/deroule', key: 'conductor.overline' },
  { to: '/ops/checkin', key: 'checkin.overline' },
  { to: '/ops/cuisine', key: 'station.kitchen' },
  { to: '/ops/bar', key: 'station.bar' },
  { to: '/ops/pos', key: 'liveBoard.screens.pos' },
  { to: '/ops/stock', key: 'liveBoard.screens.stock' },
  { to: '/admin/service', key: 'liveBoard.screens.service' },
  { to: '/ops/bilan', key: 'analytics.overline' },
]

function Header({
  live,
  name,
  updatedAt,
  stale,
  fetching,
  onRefresh,
}: {
  live: LiveOverview
  name: string
  updatedAt: number
  stale: boolean
  fetching: boolean
  onRefresh: () => void
}) {
  const { t, i18n } = useTranslation()
  const clock = useClock(live.timezone, i18n.language)
  const critical = live.alerts.filter((alert) => alert.severity === 'critical').length
  const updated = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: live.timezone,
  }).format(new Date(updatedAt))

  return (
    <header>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <Overline>{t('live.overline')}</Overline>
          <h1 className="mt-2 break-words font-display text-heading-lg text-content-primary">
            {name}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="font-mono text-heading-md tabular-nums text-content-primary">
            {clock}
          </span>
          <span
            className={cn(
              'rounded-full border px-3 py-1 text-sm tabular-nums',
              live.alerts.length === 0
                ? 'border-state-success/40 text-state-success'
                : critical > 0
                  ? 'border-state-danger text-state-danger'
                  : 'border-ember-400/40 text-ember-300',
            )}
          >
            {t('liveBoard.alertsCount', { count: live.alerts.length })}
          </span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className={cn(stale ? 'text-state-danger' : 'text-content-muted')}>
          {stale
            ? t('liveBoard.stale', { time: updated })
            : t('liveBoard.updated', { time: updated, seconds: live.refresh_seconds })}
        </span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={fetching}
          className="min-h-11 rounded-md border border-line px-3 text-content-secondary hover:border-line-strong disabled:opacity-50"
        >
          {fetching ? t('liveBoard.refreshing') : t('liveBoard.refresh')}
        </button>
        {/* Rangé avec « Actualiser » : les réglages de l'écran, pas ses chiffres. */}
        <LanguageSwitcher className="ml-auto" />
      </div>

      <nav aria-label={t('liveBoard.screensLabel')} className="mt-4 flex flex-wrap gap-2">
        {SCREENS.map((screen) => (
          <Link
            key={screen.to}
            to={screen.to}
            className="inline-flex min-h-11 items-center rounded-full border border-line-subtle px-3 text-sm text-champagne-300 hover:border-champagne-400/60"
          >
            {t(screen.key)}
          </Link>
        ))}
      </nav>
    </header>
  )
}

/** L'heure de la salle — celle du fuseau de l'édition, pas celle du téléphone. */
function useClock(timeZone: string, locale: string): string {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15_000)
    return () => window.clearInterval(timer)
  }, [])
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone }).format(
    now,
  )
}

// ── Briques ────────────────────────────────────────────────────────

function Section({
  title,
  href,
  className,
  children,
  tone = 'default',
}: {
  title: string
  href?: string
  className?: string | undefined
  children: ReactNode
  tone?: 'default' | 'alert'
}) {
  const { t } = useTranslation()
  return (
    <Card className={cn('min-w-0 p-4 sm:p-5', tone === 'alert' && 'border-state-danger', className)}>
      <div className="flex items-start justify-between gap-3">
        <h2 className="min-w-0 break-words overline text-content-muted">{title}</h2>
        {href ? (
          <Link
            to={href}
            className="shrink-0 text-sm text-champagne-300 underline underline-offset-2"
          >
            {t('liveBoard.open')}
          </Link>
        ) : null}
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  )
}

function Stat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: 'default' | 'strong' | 'alert' | 'good'
}) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-content-muted">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 break-words font-display text-heading-md tabular-nums',
          tone === 'strong' && 'text-champagne-300',
          tone === 'alert' && 'text-state-danger',
          tone === 'good' && 'text-state-success',
          tone === 'default' && 'text-content-primary',
        )}
      >
        {value}
      </dd>
      {hint ? <p className="mt-0.5 text-sm text-content-muted">{hint}</p> : null}
    </div>
  )
}

function Stats({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-4', className)}>{children}</dl>
}

function Progress({ value, max, tone = 'default' }: { value: number; max: number; tone?: 'default' | 'good' }) {
  const percent = max > 0 ? Math.min(Math.round((value * 100) / max), 100) : 0
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-bg-overlay"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
    >
      <div
        className={cn('h-full rounded-full', tone === 'good' ? 'bg-state-success' : 'bg-champagne-400')}
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}

/** Une ligne « libellé … valeur » qui passe à la ligne plutôt que de déborder. */
function Row({
  label,
  value,
  tone,
}: {
  label: ReactNode
  value: ReactNode
  tone?: 'alert' | 'muted' | undefined
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1.5">
      <span className="min-w-0 break-words text-content-secondary">{label}</span>
      <span
        className={cn(
          'tabular-nums',
          tone === 'alert' ? 'text-state-danger' : tone === 'muted' ? 'text-content-muted' : 'text-content-primary',
        )}
      >
        {value}
      </span>
    </div>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-content-muted">{children}</p>
}

// ── Déroulé ────────────────────────────────────────────────────────

function ProgramCard({
  program,
  time,
  className,
}: {
  program: OverviewProgram
  time: (iso: string) => string
  className?: string | undefined
}) {
  const { t } = useTranslation()
  const late = Math.max(program.delay_minutes ?? 0, program.overrun_minutes ?? 0)
  return (
    <Section
      title={t('liveBoard.program.title')}
      href="/ops/deroule"
      className={className}
      tone={late >= 10 ? 'alert' : 'default'}
    >
      <p className="text-sm text-content-muted">{t('screen.now')}</p>
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="min-w-0 break-words font-display text-heading-lg text-content-primary">
          {program.now?.title ?? t('conductor.nothingRunning')}
        </p>
        {program.now?.running_minutes !== null && program.now?.running_minutes !== undefined ? (
          <span className="font-mono text-heading-md tabular-nums text-champagne-300">
            {t('liveBoard.minutes', { count: program.now.running_minutes })}
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {program.delay_minutes !== null ? (
          <Badge tone={program.delay_minutes >= 10 ? 'ember' : program.delay_minutes > 0 ? 'champagne' : 'success'}>
            {program.delay_minutes > 0
              ? t('liveBoard.program.late', { count: program.delay_minutes })
              : program.delay_minutes < 0
                ? t('liveBoard.program.early', { count: -program.delay_minutes })
                : t('liveBoard.program.onTime')}
          </Badge>
        ) : null}
        {program.overrun_minutes ? (
          <Badge tone="ember">{t('liveBoard.program.overrun', { count: program.overrun_minutes })}</Badge>
        ) : null}
        <Badge>
          {t('liveBoard.program.steps', { done: program.steps_done, total: program.steps_total })}
        </Badge>
      </div>

      {program.next ? (
        <p className="mt-4 break-words text-sm text-content-secondary">
          {t('screen.next')} ·{' '}
          {program.next.starts_at ? (
            <span className="tabular-nums text-content-primary">{time(program.next.starts_at)} </span>
          ) : null}
          {program.next.title}
        </p>
      ) : null}
    </Section>
  )
}

// ── Alertes ────────────────────────────────────────────────────────

const SEVERITY_DOT = {
  critical: 'bg-state-danger',
  warning: 'bg-ember-400',
  info: 'bg-state-info',
} as const

function AlertsCard({
  alerts,
  money,
  className,
}: {
  alerts: OverviewAlert[]
  money: Money
  className?: string | undefined
}) {
  const { t } = useTranslation()
  return (
    <Section
      title={t('liveBoard.alerts.title')}
      className={className}
      tone={alerts.some((alert) => alert.severity === 'critical') ? 'alert' : 'default'}
    >
      {alerts.length === 0 ? (
        <p className="text-state-success">{t('liveBoard.alerts.none')}</p>
      ) : (
        <ul className="divide-y divide-line-subtle">
          {alerts.map((alert, index) => (
            <li key={`${alert.code}-${index}`} className="flex items-start gap-3 py-2.5">
              <span
                aria-hidden
                className={cn('mt-2 h-2 w-2 shrink-0 rounded-full', SEVERITY_DOT[alert.severity])}
              />
              <p className="min-w-0 flex-1 break-words text-sm text-content-primary">
                <span className="sr-only">{t(`liveBoard.severity.${alert.severity}`)} · </span>
                {alertText(t, alert, money)}
              </p>
              {alert.href ? (
                <Link
                  to={alert.href}
                  className="shrink-0 text-sm text-champagne-300 underline underline-offset-2"
                >
                  {t('liveBoard.fix')}
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

function alertText(t: Translate, alert: OverviewAlert, money: Money): string {
  const context = alert.context
  const labels = (key: string) =>
    Array.isArray(context[key]) ? (context[key] as unknown[]).map(String).join(', ') : ''
  const station = context.station === 'kitchen' ? t('station.kitchen') : t('station.bar')
  return t(`liveBoard.alert.${alert.code}`, {
    defaultValue: alert.code,
    count: alert.count,
    station,
    minutes: Number(context.minutes ?? 0),
    limit: Number(context.limit ?? 0),
    late: Number(context.late ?? 0),
    amount: typeof context.total_minor === 'number' ? money(context.total_minor) : '',
    labels: labels('labels') || labels('names') || labels('orders') || labels('registers'),
  }) as string
}

// ── Entrées ────────────────────────────────────────────────────────

function EntriesCard({ entries }: { entries: OverviewEntries }) {
  const { t } = useTranslation()
  return (
    <Section title={t('liveBoard.entries.title')} href="/ops/checkin">
      <Stats>
        <Stat
          label={t('liveBoard.entries.entered')}
          value={entries.entered}
          hint={t('liveBoard.entries.recent', { count: entries.last_15min })}
          tone="strong"
        />
        <Stat
          label={t('liveBoard.entries.tickets')}
          value={`${entries.checked_in} / ${entries.expected}`}
          hint={t('live.rate', { rate: entries.rate })}
        />
        <Stat
          label={t('liveBoard.entries.door')}
          value={entries.door_people}
          hint={t('liveBoard.entries.doorSales', { count: entries.door_sales })}
        />
        <Stat label={t('liveBoard.entries.remaining')} value={entries.remaining} />
      </Stats>
      <div className="mt-4">
        <Progress value={entries.checked_in} max={entries.expected} />
      </div>
    </Section>
  )
}

// ── Postes ─────────────────────────────────────────────────────────

function StationsCard({ stations }: { stations: OverviewStations }) {
  const { t } = useTranslation()
  const limit = stations.wait_alert_minutes
  return (
    <Section
      title={t('liveBoard.stations.title')}
      tone={stations.stations.some((station) => station.late > 0) ? 'alert' : 'default'}
    >
      <div className="space-y-5">
        {stations.stations.map((station) => {
          const late = (station.oldest_wait_minutes ?? 0) >= limit
          return (
            <div key={station.station} className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-display text-heading-sm text-content-primary">
                  {station.station === 'kitchen' ? t('station.kitchen') : t('station.bar')}
                </p>
                <Link
                  to={station.station === 'kitchen' ? '/ops/cuisine' : '/ops/bar'}
                  className="text-sm text-champagne-300 underline underline-offset-2"
                >
                  {t('liveBoard.open')}
                </Link>
              </div>
              <Stats className="mt-3">
                <Stat
                  label={t('liveBoard.stations.waiting')}
                  value={station.waiting}
                  hint={
                    station.late > 0
                      ? t('liveBoard.stations.late', { count: station.late, limit })
                      : undefined
                  }
                  tone={station.late > 0 ? 'alert' : 'default'}
                />
                <Stat
                  label={t('liveBoard.stations.oldest')}
                  value={
                    station.oldest_wait_minutes !== null
                      ? t('liveBoard.minutes', { count: station.oldest_wait_minutes })
                      : '—'
                  }
                  tone={late ? 'alert' : 'default'}
                />
                <Stat label={t('liveBoard.stations.unpaid')} value={station.unpaid} />
                <Stat label={t('liveBoard.stations.done')} value={station.done} />
                <Stat
                  label={t('liveBoard.stations.prepHour')}
                  value={
                    station.avg_prep_minutes_hour !== null
                      ? t('liveBoard.minutes', { count: station.avg_prep_minutes_hour })
                      : '—'
                  }
                  hint={
                    station.avg_prep_minutes !== null
                      ? t('liveBoard.stations.prepEvening', {
                          value: t('liveBoard.minutes', { count: station.avg_prep_minutes }),
                        })
                      : undefined
                  }
                />
                <Stat label={t('liveBoard.stations.cancelled')} value={station.cancelled} />
              </Stats>
            </div>
          )
        })}
      </div>
    </Section>
  )
}

function WaitersCard({ stations, money }: { stations: OverviewStations; money: Money }) {
  const { t } = useTranslation()
  return (
    <Section title={t('liveBoard.waiters.title')} href="/admin/service">
      {stations.waiters.length === 0 ? (
        <Empty>{t('liveBoard.waiters.none')}</Empty>
      ) : (
        <ul className="divide-y divide-line-subtle">
          {stations.waiters.map((waiter) => (
            <li key={waiter.waiter_id ?? 'none'} className="py-2.5">
              <p className="break-words text-content-primary">
                {waiter.waiter_id ? waiter.name : t('liveBoard.waiters.unassigned')}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <span className={waiter.unpaid > 0 ? 'text-ember-300' : 'text-content-muted'}>
                  {t('liveBoard.waiters.unpaid', { count: waiter.unpaid })}
                  {waiter.unpaid > 0 ? ` · ${money(waiter.unpaid_minor)}` : ''}
                </span>
                <span className="text-content-muted">
                  {t('liveBoard.waiters.inProgress', { count: waiter.in_progress })}
                </span>
                <span className="text-content-muted">
                  {t('liveBoard.waiters.done', { count: waiter.done })}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

// ── Caisses ────────────────────────────────────────────────────────

function TillsCard({
  tills,
  money,
  time,
}: {
  tills: OverviewTills
  money: Money
  time: (iso: string) => string
}) {
  const { t } = useTranslation()
  return (
    <Section title={t('liveBoard.tills.title')} href="/admin/service">
      <Stats>
        <Stat
          label={t('liveBoard.tills.open')}
          value={`${tills.open_count} / ${tills.tills.length}`}
        />
        <Stat
          label={t('liveBoard.tills.expectedTotal')}
          value={money(tills.expected_total_minor)}
          tone="strong"
        />
      </Stats>
      {tills.tills.length === 0 ? (
        <div className="mt-4">
          <Empty>{t('liveBoard.tills.none')}</Empty>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-line-subtle">
          {tills.tills.map((till) => (
            <TillRow key={till.register_id} till={till} money={money} time={time} />
          ))}
        </ul>
      )}
    </Section>
  )
}

function TillRow({
  till,
  money,
  time,
}: {
  till: OverviewTill
  money: Money
  time: (iso: string) => string
}) {
  const { t } = useTranslation()
  const post = till.station && till.station !== 'none' ? t(`liveBoard.post.${till.station}`) : null
  // « Cuisine · Cuisine » ne dit rien de plus : le poste n'est rappelé que
  // quand le nom de la caisse ne le dit pas déjà.
  const station = post && post.toLowerCase() !== till.name.toLowerCase() ? post : null
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words text-content-primary">
          {till.name}
          {station ? <span className="text-content-muted"> · {station}</span> : null}
        </p>
        <Badge tone={till.state === 'open' ? 'success' : till.state === 'closed' ? 'neutral' : 'neutral'}>
          {t(`liveBoard.tills.state.${till.state}`)}
        </Badge>
      </div>
      {till.holder ? (
        <p className="mt-0.5 break-words text-sm text-content-muted">
          {till.holder}
          {till.opened_at ? ` · ${t('liveBoard.tills.since', { time: time(till.opened_at) })}` : ''}
        </p>
      ) : null}
      {till.state !== 'idle' ? (
        <div className="mt-2 text-sm">
          <Row label={t('liveBoard.tills.float')} value={money(till.opening_float_minor)} />
          <Row label={t('liveBoard.tills.cashSales')} value={money(till.cash_sales_minor)} />
          {till.handovers_minor ? (
            <Row label={t('liveBoard.tills.handovers')} value={money(till.handovers_minor)} />
          ) : null}
          <Row label={t('liveBoard.tills.expected')} value={money(till.expected_minor)} />
          <Row label={t('liveBoard.tills.paypal')} value={money(till.paypal_minor)} />
          {till.card_minor ? (
            <Row label={t('liveBoard.tills.card')} value={money(till.card_minor)} />
          ) : null}
          {till.state === 'closed' && till.counted_minor !== null ? (
            <Row
              label={t('liveBoard.tills.variance')}
              value={money(till.variance_minor ?? 0)}
              tone={(till.variance_minor ?? 0) !== 0 ? 'alert' : undefined}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function HoldersCard({ tills, money }: { tills: OverviewTills; money: Money }) {
  const { t } = useTranslation()
  const holders = tills.holders ?? []
  return (
    <Section title={t('liveBoard.holders.title')} href="/ops/pos">
      <Stat
        label={t('liveBoard.holders.total')}
        value={money(tills.held_total_minor ?? 0)}
        tone={holders.length ? 'strong' : 'good'}
      />
      <p className="mt-2 text-sm text-content-muted">{t('liveBoard.holders.help')}</p>
      {holders.length === 0 ? (
        <div className="mt-3">
          <Empty>{t('liveBoard.holders.none')}</Empty>
        </div>
      ) : (
        <div className="mt-3 divide-y divide-line-subtle">
          {holders.map((holder) => (
            <Row key={holder.user_id} label={holder.name} value={money(holder.held_minor)} />
          ))}
        </div>
      )}
    </Section>
  )
}

// ── Ventes ─────────────────────────────────────────────────────────

const FAMILIES = ['tickets', 'food', 'drinks', 'tables', 'other'] as const

function SalesCard({ sales, money }: { sales: OverviewSales; money: Money }) {
  const { t } = useTranslation()
  const goals = sales.objectives
  const methods = Object.entries(sales.by_method)
  return (
    <Section title={t('liveBoard.sales.title')} href="/ops/bilan">
      <Stats>
        <Stat label={t('liveBoard.sales.net')} value={money(sales.net_minor)} tone="strong" />
        <Stat label={t('liveBoard.sales.onsite')} value={money(sales.onsite_minor)} />
        <Stat
          label={t('liveBoard.sales.outstanding')}
          value={money(sales.outstanding_minor)}
          tone={sales.outstanding_minor > 0 ? 'alert' : 'default'}
        />
        <Stat label={t('liveBoard.sales.refunded')} value={money(sales.refunded_minor)} />
      </Stats>

      <p className="mt-5 text-sm text-content-muted">{t('liveBoard.sales.byFamily')}</p>
      <div className="mt-1 divide-y divide-line-subtle text-sm">
        {FAMILIES.filter(
          (family) => family !== 'other' || (sales.by_family[family] ?? 0) > 0,
        ).map((family) => (
          <Row
            key={family}
            label={t(`liveBoard.sales.family.${family}`)}
            value={money(sales.by_family[family] ?? 0)}
          />
        ))}
      </div>

      <p className="mt-4 text-sm text-content-muted">{t('liveBoard.sales.byMethod')}</p>
      <div className="mt-1 divide-y divide-line-subtle text-sm">
        {methods.length === 0 ? (
          <Row label="—" value={money(0)} tone="muted" />
        ) : (
          methods.map(([method, amount]) => (
            <Row
              key={method}
              label={t(`liveBoard.sales.method.${method}`, { defaultValue: method })}
              value={money(amount)}
            />
          ))
        )}
      </div>

      {goals ? (
        <div className="mt-5 rounded-md border border-line-subtle p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-content-muted">{t('liveBoard.sales.goal')}</p>
            <Link to="/admin/objectifs" className="text-sm text-champagne-300 underline underline-offset-2">
              {t('liveBoard.open')}
            </Link>
          </div>
          <p className="mt-1 break-words tabular-nums text-content-primary">
            {money(goals.realized_minor)} / {money(goals.goal_minor)}
            {goals.percent !== null ? (
              <span className="text-champagne-300"> · {goals.percent} %</span>
            ) : null}
          </p>
          <div className="mt-2">
            <Progress value={goals.realized_minor} max={goals.goal_minor} tone="good" />
          </div>
          {goals.lines.length ? (
            <ul className="mt-3 space-y-2 text-sm">
              {goals.lines.map((line, index) => (
                <li key={`${line.label}-${index}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="min-w-0 break-words text-content-secondary">{line.label}</span>
                    <span className="tabular-nums text-content-primary">{line.percent} %</span>
                  </div>
                  <div className="mt-1">
                    <Progress value={line.sold_minor} max={line.target_minor} />
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Section>
  )
}

// ── Stock ──────────────────────────────────────────────────────────

function StockCard({ stock }: { stock: NonNullable<LiveOverview['stock']> }) {
  const { t } = useTranslation()
  return (
    <Section
      title={t('liveBoard.stock.title')}
      href="/ops/stock"
      tone={stock.sold_out.length ? 'alert' : 'default'}
    >
      <p className="text-sm text-content-muted">
        {t('liveBoard.stock.soldOut', { count: stock.sold_out.length })}
      </p>
      {stock.sold_out.length ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {stock.sold_out.map((line) => (
            <li
              key={`${line.kind}-${line.label}`}
              className="max-w-full break-words rounded-full border border-state-danger/50 px-3 py-1 text-sm text-state-danger"
            >
              {line.label}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-state-success">{t('liveBoard.stock.noneSoldOut')}</p>
      )}

      <p className="mt-4 text-sm text-content-muted">
        {t('liveBoard.stock.low', { count: stock.low.length })}
      </p>
      {stock.low.length ? (
        <div className="mt-1 divide-y divide-line-subtle text-sm">
          {stock.low.map((line) => (
            <Row
              key={`${line.kind}-${line.label}`}
              label={line.label}
              value={t('liveBoard.stock.remaining', {
                remaining: line.remaining ?? 0,
                threshold: line.threshold ?? 0,
              })}
            />
          ))}
        </div>
      ) : (
        <Empty>{t('liveBoard.stock.noneLow')}</Empty>
      )}
    </Section>
  )
}

// ── Tables ─────────────────────────────────────────────────────────

function TablesCard({ tables }: { tables: OverviewTables }) {
  const { t } = useTranslation()
  return (
    <Section title={t('live.tables')} href="/admin/service">
      <Stats>
        <Stat
          label={t('liveBoard.tables.occupied')}
          value={`${tables.occupied} / ${tables.total}`}
          tone="strong"
        />
        <Stat label={t('liveBoard.tables.free')} value={tables.free} />
        <Stat label={t('liveBoard.tables.withWaiter')} value={`${tables.with_waiter} / ${tables.total}`} />
        <Stat label={t('liveBoard.tables.outOfService')} value={tables.out_of_service} />
        {/* Le placement de l'hôtesse : qui est assis, qui attend encore. */}
        <Stat label={t('liveBoard.tables.seated')} value={tables.seated} />
        <Stat
          label={t('liveBoard.tables.waiting')}
          value={tables.arrived_waiting}
          tone={tables.waiting_late.length ? 'alert' : 'default'}
        />
      </Stats>

      {tables.waiting_late.length ? (
        <div className="mt-4 rounded-md border border-ember-400 p-3">
          <p className="text-sm text-ember-300">
            {t('liveBoard.tables.waitingLate', { count: tables.waiting_late.length })}
          </p>
          <p className="mt-1 break-words text-sm text-content-primary">
            {tables.waiting_late.join(' · ')}
          </p>
          <Link
            to="/ops/service?tab=placement"
            className="mt-2 inline-block text-sm text-champagne-300 underline underline-offset-2"
          >
            {t('liveBoard.tables.openPlacement')}
          </Link>
        </div>
      ) : null}

      {tables.unassigned.length ? (
        <div className="mt-4 rounded-md border border-ember-400 p-3">
          <p className="text-sm text-ember-300">
            {t('liveBoard.tables.unassigned', { count: tables.unassigned.length })}
          </p>
          <p className="mt-1 break-words text-sm text-content-primary">
            {tables.unassigned.join(' · ')}
          </p>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className={tables.requests_new ? 'text-ember-300' : 'text-content-muted'}>
          {t('liveBoard.tables.requests', {
            fresh: tables.requests_new,
            quoted: tables.requests_quoted,
          })}
        </span>
        <Link to="/admin/demandes-tables" className="text-champagne-300 underline underline-offset-2">
          {t('liveBoard.open')}
        </Link>
      </div>
    </Section>
  )
}

function FloorDetails({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Le plan détaillé ne se charge qu'à l'ouverture : la plupart des soirs, le
  // compteur suffit, et chaque requête en moins compte sur ce réseau-là.
  const floor = useFloorPlan(eventId, open)
  return (
    <details
      className="mt-6 rounded-lg border border-line-subtle bg-bg-elevated"
      onToggle={(toggle) => setOpen((toggle.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer select-none px-4 py-3 text-content-primary sm:px-5">
        {t('live.floor')}
      </summary>
      <div className="px-4 pb-4 sm:px-5">
        {!floor.data ? (
          <Skeleton className="h-24 w-full" />
        ) : floor.data.tables.length === 0 ? (
          <Empty>{t('liveBoard.tables.noFloor')}</Empty>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {floor.data.tables.map((table) => (
              <li
                key={table.id}
                className={cn(
                  'min-w-0 rounded-md border p-3 text-center',
                  table.occupied ? 'border-champagne-400/60' : 'border-line-subtle opacity-70',
                )}
              >
                <p className="font-display text-heading-sm text-content-primary">{table.table_number}</p>
                {table.table_name ? (
                  <p className="break-words text-sm text-content-secondary">{table.table_name}</p>
                ) : null}
                <p className="mt-1 break-words text-sm text-content-muted">
                  {table.occupied
                    ? `${table.party_size ?? '—'} p · ${table.customer_name ?? ''}`
                    : t('live.free')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  )
}

// ── Concours et votes ──────────────────────────────────────────────

function ContestCard({ contest, time }: { contest: OverviewContest; time: (iso: string) => string }) {
  const { t } = useTranslation()
  return (
    <Section title={t('liveBoard.contest.title')} href="/ops/deroule">
      <Stats>
        <Stat label={t('live.round')} value={contest.running_round ?? '—'} />
        <Stat label={t('liveBoard.contest.candidates')} value={contest.candidates_remaining} />
        <Stat
          label={t('liveBoard.contest.ballots')}
          value={contest.votes_total}
          hint={t('live.votesRecent', { count: contest.votes_last_5min })}
        />
        <Stat label={t('liveBoard.contest.present')} value={contest.present} />
      </Stats>

      <p className="mt-5 text-sm text-content-muted">
        {t('liveBoard.contest.open', { count: contest.open_votes.length })}
      </p>
      {contest.open_votes.length === 0 ? (
        <Empty>{t('liveBoard.contest.noneOpen')}</Empty>
      ) : (
        <ul className="mt-2 space-y-4">
          {contest.open_votes.map((vote) => (
            <li key={vote.id} className="min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="min-w-0 break-words text-content-primary">{vote.name}</p>
                {vote.closes_at ? (
                  <span className="text-sm tabular-nums text-content-muted">
                    {t('liveBoard.contest.closes', { time: time(vote.closes_at) })}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-content-secondary">
                {t('liveBoard.contest.turnout', {
                  voters: vote.voters,
                  votes: vote.votes,
                  rate: vote.turnout ?? 0,
                })}
              </p>
              <div className="mt-1">
                <Progress value={vote.voters} max={contest.present} />
              </div>
              {vote.jury_expected > 0 ? (
                <div className="mt-2">
                  <p className="text-sm text-content-secondary">
                    {t('liveBoard.contest.jury', {
                      scored: vote.jury_scored,
                      expected: vote.jury_expected,
                      jurors: vote.jurors,
                    })}
                  </p>
                  <div className="mt-1">
                    <Progress value={vote.jury_scored} max={vote.jury_expected} tone="good" />
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
